import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../context/AuthContext';
import { Exam } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const STATE_BADGE = { draft: 'bg-slate-100 text-slate-500', submitted: 'bg-amber-100 text-amber-700', moderated: 'bg-blue-100 text-blue-700', published: 'bg-green-100 text-green-700' };
const NEXT = { draft: ['submitted', 'Submit for moderation'], submitted: ['moderated', 'Mark as moderated'], moderated: ['published', 'Publish to students'] };

const Distribution = ({ dist }) => {
  const max = Math.max(1, ...dist);
  return (
    <div className="flex items-end gap-1 h-14" data-testid="grid-distribution" aria-label="Mark distribution">
      {dist.map((n, i) => (
        <div key={i} className="flex-1 text-center">
          <div className="bg-orange-400/80 rounded-t mx-auto w-full" style={{ height: `${(n / max) * 40}px` }} title={`${i * 10}-${i * 10 + 9}%: ${n}`} />
          <span className="text-[9px] text-slate-400">{i * 10}</span>
        </div>
      ))}
    </div>
  );
};

const GridTab = ({ sections }) => {
  const qc = useQueryClient();
  const [sectionId, setSectionId] = useState('');
  const [comp, setComp] = useState({ name: '', component_type: 'exam', weight: '', max_marks: '100' });
  const [importText, setImportText] = useState('');
  const [preview, setPreview] = useState(null);
  const { data: grid } = useQuery({ enabled: !!sectionId, queryKey: ['gb-grid', sectionId], queryFn: () => api.get(`/gb/grid?section_id=${sectionId}`).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['gb-grid', sectionId] });
  const locked = grid && grid.state === 'published';
  const saveCell = async (student_id, component_id, v) => {
    try { await api.put('/gb/marks', { section_id: sectionId, entries: [{ student_id, component_id, marks: v === '' ? null : parseFloat(v) }] }); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addComp = async () => {
    try {
      await api.post('/gb/components', { section_id: sectionId, name: comp.name, component_type: comp.component_type, weight: parseFloat(comp.weight), max_marks: parseFloat(comp.max_marks) });
      toast.success('Assessment added'); setComp({ ...comp, name: '', weight: '' }); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const parseRows = () => {
    const lines = importText.trim().split('\n').map((l) => l.split(',').map((x) => x.trim()));
    const head = lines[0];
    return lines.slice(1).map((cells) => Object.fromEntries(head.map((hh, i) => [i === 0 ? 'student' : hh, cells[i]])));
  };
  const doImport = async (commit) => {
    try {
      const { data } = await api.post('/gb/import', { section_id: sectionId, rows: parseRows(), commit });
      if (!commit) { setPreview(data); return; }
      toast.success(`Imported ${data.committed} marks`); setPreview(null); setImportText(''); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const transitionTo = async () => {
    const [to, label] = NEXT[grid.state] || [];
    if (!to) return;
    try { await api.post('/gb/state/transition', { section_id: sectionId, to_state: to, reason: label }); toast.success(label); refresh(); qc.invalidateQueries({ queryKey: ['gb-queue'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onKey = (e) => {
    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const cells = Array.from(document.querySelectorAll('[data-gridcell]'));
    const i = cells.indexOf(e.target);
    const cols = (grid?.components || []).length;
    const j = e.key === 'ArrowUp' ? i - cols : i + cols;
    if (cells[j]) cells[j].focus();
  };
  return (
    <Card testid="gb-grid-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="grid-section-select" className="border border-slate-200 rounded-md text-sm px-2 py-2" value={sectionId} onChange={(e) => { setSectionId(e.target.value); setPreview(null); }}>
          <option value="">Pick a section</option>
          {(sections || []).map((s) => <option key={s.id} value={s.id}>{s.course_code} {s.section_code}</option>)}
        </select>
        {grid && <span className={`text-[10px] px-2 py-0.5 rounded capitalize ${STATE_BADGE[grid.state]}`} data-testid="grid-state">{grid.state}</span>}
        {grid && <span className="text-xs text-slate-400">Weights total {grid.total_weight}%</span>}
        {grid && NEXT[grid.state] && <Button data-testid="grid-transition-btn" size="sm" className="ms-auto bg-slate-900 hover:bg-slate-800" onClick={transitionTo}>{NEXT[grid.state][1]}</Button>}
      </div>
      {grid && !locked && (
        <div className="flex flex-wrap gap-2 mb-3 items-center border border-dashed border-slate-200 rounded-lg p-2">
          <Input data-testid="comp-name" className="h-8 w-36" placeholder="Assessment name" value={comp.name} onChange={(e) => setComp({ ...comp, name: e.target.value })} />
          <select data-testid="comp-type" className="border border-slate-200 rounded-md text-xs px-2 h-8" value={comp.component_type} onChange={(e) => setComp({ ...comp, component_type: e.target.value })}>
            {['exam', 'quiz', 'assignment', 'project', 'practical', 'participation'].map((t) => <option key={t}>{t}</option>)}
          </select>
          <Input data-testid="comp-weight" className="h-8 w-24" placeholder="Weight %" value={comp.weight} onChange={(e) => setComp({ ...comp, weight: e.target.value })} />
          <Input data-testid="comp-max" className="h-8 w-24" placeholder="Max marks" value={comp.max_marks} onChange={(e) => setComp({ ...comp, max_marks: e.target.value })} />
          <Button data-testid="comp-add-btn" size="sm" variant="outline" disabled={!comp.name || !comp.weight} onClick={addComp}>Add assessment</Button>
        </div>
      )}
      {grid && grid.components.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="marks-grid">
              <thead><tr className="text-left border-b border-slate-100">
                <th className="py-1.5 pr-2 font-mono-label text-[10px] uppercase text-slate-400">Student</th>
                {grid.components.map((c) => <th key={c.id} className="font-mono-label text-[10px] uppercase text-slate-400 pr-2">{c.name} <span className="normal-case">/{c.max_marks} · {c.weight}%</span></th>)}
                <th className="font-mono-label text-[10px] uppercase text-slate-400">Total %</th>
              </tr></thead>
              <tbody>{grid.rows.map((r) => (
                <tr key={r.student_id} className="border-b border-slate-50">
                  <td className="py-1 pr-2 font-medium whitespace-nowrap">{r.student_name}</td>
                  {grid.components.map((c) => (
                    <td key={c.id} className="pr-2">
                      <input data-gridcell data-testid={`cell-${r.student_id}-${c.id}`} type="number" disabled={locked}
                        className="w-20 border border-slate-200 rounded px-1.5 py-1 text-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-50"
                        defaultValue={r.cells[c.id] ?? ''} onKeyDown={onKey}
                        onBlur={(e) => e.target.value !== String(r.cells[c.id] ?? '') && saveCell(r.student_id, c.id, e.target.value)} />
                    </td>
                  ))}
                  <td className={`font-semibold ${r.total_pct < 33 ? 'text-rose-600' : ''}`}>{r.total_pct}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="mt-3"><p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Distribution</p><Distribution dist={grid.distribution} /></div>
        </>
      )}
      {grid && !locked && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-xs font-medium mb-1">Import from a spreadsheet - paste CSV (first column header must be "student", other headers match assessment names)</p>
          <textarea data-testid="import-text" className="w-full border border-slate-200 rounded-md text-xs font-mono p-2" rows={3}
            placeholder={'student,Midterm,Final\nAarav Gupta,45,88'} value={importText} onChange={(e) => setImportText(e.target.value)} />
          <div className="flex gap-2 mt-1">
            <Button data-testid="import-preview-btn" size="sm" variant="outline" disabled={!importText.trim()} onClick={() => doImport(false)}>Preview and validate</Button>
            {preview && <Button data-testid="import-commit-btn" size="sm" disabled={preview.valid !== preview.total} onClick={() => doImport(true)}>Commit {preview.valid} of {preview.total} rows</Button>}
          </div>
          {preview && preview.preview.filter((p) => !p.ok).map((p, i) => <p key={i} className="text-xs text-rose-600 mt-1" data-testid="import-issue">{p.student}: {p.issues.join('; ')}</p>)}
        </div>
      )}
    </Card>
  );
};

const QueueTab = () => {
  const qc = useQueryClient();
  const { data: queue } = useQuery({ queryKey: ['gb-queue'], queryFn: () => api.get('/gb/queue').then((r) => r.data) });
  const move = async (q) => {
    const [to, label] = NEXT[q.state] || [];
    if (!to) return;
    try { await api.post('/gb/state/transition', { section_id: q.section_id, to_state: to, reason: label }); toast.success(label); qc.invalidateQueries({ queryKey: ['gb-queue'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="gb-queue-tab">
      {(queue || []).map((q) => (
        <div key={q.id} className="flex items-center justify-between gap-2 py-2 border-t border-slate-50 text-sm" data-testid={`queue-row-${q.section_code}`}>
          <span><b>{q.section_code}</b> <span className={`text-[10px] px-2 py-0.5 rounded capitalize ${STATE_BADGE[q.state]}`}>{q.state}</span>
            <span className="block text-xs text-slate-400">{(q.transitions || []).map((t) => `${t.state} by ${t.by} at ${(t.at || '').slice(0, 16).replace('T', ' ')}`).join(' → ')}</span></span>
          {NEXT[q.state] && <Button size="sm" variant="outline" data-testid={`queue-move-${q.section_code}`} onClick={() => move(q)}>{NEXT[q.state][1]}</Button>}
        </div>
      ))}
      {(queue || []).length === 0 && <p className="text-sm text-slate-400">Nothing is moving through moderation yet.</p>}
    </Card>
  );
};

const ChangesTab = () => {
  const qc = useQueryClient();
  const { data: changes } = useQuery({ queryKey: ['gb-changes'], queryFn: () => api.get('/gb/grade-changes').then((r) => r.data) });
  const { data: reevals } = useQuery({ queryKey: ['gb-reevals'], queryFn: () => api.get('/gb/reevals').then((r) => r.data) });
  const decide = async (id, approve) => {
    try { await api.post(`/gb/grade-changes/${id}/decide`, { approve, reason: approve ? 'Approved' : 'Rejected' }); toast.success(approve ? 'Grade changed - original kept on record' : 'Request rejected'); qc.invalidateQueries({ queryKey: ['gb-changes'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const complete = async (r) => {
    const revised = window.prompt(`Re-evaluation of ${r.course_code} (${r.original_value}). New value, or leave empty if unchanged:`);
    if (revised === null) return;
    try {
      await api.post(`/gb/reevals/${r.id}/complete`, revised.trim() ? { outcome: 'revised', revised_value: revised.trim(), reason: 'Re-marked' } : { outcome: 'unchanged', reason: 'Original mark confirmed' });
      toast.success('Re-evaluation completed'); qc.invalidateQueries({ queryKey: ['gb-reevals'] }); qc.invalidateQueries({ queryKey: ['gb-changes'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card testid="gb-changes-list">
        <p className="font-display font-bold mb-2">Grade change requests</p>
        {(changes || []).map((c) => (
          <div key={c.id} className="py-2 border-t border-slate-50 text-sm" data-testid={`change-row-${c.id}`}>
            <p><b>{c.student_name}</b> {c.course_code}: {c.original_value} → <b>{c.new_value}</b> <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${c.status === 'requested' ? 'bg-amber-100 text-amber-700' : c.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>{c.status}</span></p>
            <p className="text-xs text-slate-500">{c.reason} · by {c.requested_by}</p>
            {c.status === 'requested' && (
              <span className="flex gap-2 mt-1">
                <button className="text-xs text-green-700 underline" data-testid={`change-approve-${c.id}`} onClick={() => decide(c.id, true)}>Approve</button>
                <button className="text-xs text-rose-500 underline" data-testid={`change-reject-${c.id}`} onClick={() => decide(c.id, false)}>Reject</button>
              </span>
            )}
          </div>
        ))}
        {(changes || []).length === 0 && <p className="text-sm text-slate-400">No grade change requests.</p>}
      </Card>
      <Card testid="gb-reevals-list">
        <p className="font-display font-bold mb-2">Re-evaluation requests</p>
        {(reevals || []).map((r) => (
          <div key={r.id} className="py-2 border-t border-slate-50 text-sm flex items-start justify-between gap-2" data-testid={`reeval-row-${r.id}`}>
            <span><b>{r.student_name}</b> {r.course_code} ({r.original_value}) <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${r.status === 'requested' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{r.status}</span>
              <span className="block text-xs text-slate-500">{r.reason}{r.fee ? ` · fee ${r.fee}` : ''}{r.outcome ? ` · outcome: ${r.outcome}${r.revised_value ? ` (${r.revised_value})` : ''}` : ''}</span></span>
            {r.status !== 'completed' && <button className="text-xs text-blue-600 underline shrink-0" data-testid={`reeval-complete-${r.id}`} onClick={() => complete(r)}>Record outcome</button>}
          </div>
        ))}
        {(reevals || []).length === 0 && <p className="text-sm text-slate-400">No re-evaluation requests.</p>}
      </Card>
    </div>
  );
};

const CredentialsTab = ({ students }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canIssue = ['global_admin', 'school_admin', 'principal', 'registrar'].includes(user?.role);
  const { data: creds } = useQuery({ queryKey: ['gb-creds'], queryFn: () => api.get('/gb/credentials').then((r) => r.data) });
  const { data: issuedT } = useQuery({ queryKey: ['transcript-log'], queryFn: () => api.get('/acad/transcripts/issued').then((r) => r.data).catch(() => []) });
  const [f, setF] = useState({ student_id: '', title: '', credential_type: 'certificate' });
  const [tSid, setTSid] = useState('');
  const issue = async () => {
    try { const { data } = await api.post('/gb/credentials', f); toast.success(`Issued ${data.serial} - verification code ${data.verify_code}`); setF({ ...f, title: '' }); qc.invalidateQueries({ queryKey: ['gb-creds'] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card testid="gb-cred-issue">
        <p className="font-display font-bold mb-2">Issue a credential</p>
        {!canIssue && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-2" data-testid="cred-no-permission">Only admins, principals and registrars can issue credentials - you can view the issued list below.</p>}
        {canIssue && <div className="space-y-2">
          <select data-testid="cred-student" className="border border-slate-200 rounded-md text-sm px-2 py-2 w-full" value={f.student_id} onChange={(e) => setF({ ...f, student_id: e.target.value })}>
            <option value="">Pick a student</option>
            {(students || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="cred-type" className="border border-slate-200 rounded-md text-sm px-2 py-2 w-full" value={f.credential_type} onChange={(e) => setF({ ...f, credential_type: e.target.value })}>
            {['certificate', 'diploma', 'degree', 'transcript-copy', 'award'].map((t) => <option key={t}>{t}</option>)}
          </select>
          <Input data-testid="cred-title" placeholder="Title (e.g. Certificate of merit)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Button data-testid="cred-issue-btn" className="bg-slate-900 hover:bg-slate-800" disabled={!f.student_id || !f.title.trim()} onClick={issue}>Issue credential</Button>
        </div>}
        <div className="mt-4 max-h-56 overflow-auto">
          {(creds || []).map((c) => (
            <div key={c.id} className="py-1.5 border-t border-slate-50 text-sm" data-testid={`cred-row-${c.serial}`}>
              <span className="flex items-center justify-between gap-2">
                <span><b>{c.serial}</b> {c.title} — {c.student_name}</span>
                <a data-testid={`cred-pdf-${c.serial}`} href={`${API}/api/gb/credentials/${c.id}/pdf`} target="_blank" rel="noreferrer"
                  className="text-xs border border-slate-200 rounded px-2 py-0.5 hover:border-orange-500 shrink-0">PDF</a>
              </span>
              <span className="block text-xs text-slate-400">Verification code <span className="font-mono">{c.verify_code}</span> — anyone can check it at /verify</span>
            </div>
          ))}
        </div>
      </Card>
      <Card testid="gb-transcript-log">
        <p className="font-display font-bold mb-2">Generate a transcript</p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select data-testid="transcript-gen-student" className="border border-slate-200 rounded-md text-sm px-2 py-2" value={tSid} onChange={(e) => setTSid(e.target.value)}>
            <option value="">Pick a student</option>
            {(students || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {tSid && (
            <>
              {canIssue && <a data-testid="transcript-gen-official" href={`${API}/api/acad/transcript/${tSid}/pdf`} target="_blank" rel="noreferrer"
                onClick={() => setTimeout(() => qc.invalidateQueries({ queryKey: ['transcript-log'] }), 1500)}
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 hover:bg-slate-800">Official PDF</a>}
              <a data-testid="transcript-gen-unofficial" href={`${API}/api/acad/transcript/${tSid}/pdf?official=false`} target="_blank" rel="noreferrer"
                className="text-sm border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">Unofficial copy</a>
            </>
          )}
        </div>
        <p className="text-xs text-slate-400 mb-3">Official copies get a serial, a verification code and an entry in the issue log below. Unofficial copies are watermarked and carry no code.</p>
        <p className="font-display font-bold mb-2">Transcripts issued</p>
        {(issuedT || []).map((t) => (
          <p key={t.id} className="py-1.5 border-t border-slate-50 text-sm"><b>{t.serial}</b> {t.student_name} <span className="text-xs text-slate-400">by {t.issued_by} · {(t.issued_at || '').slice(0, 10)}</span>
            {t.verify_code && <span className="block text-xs text-slate-400">Verification code <span className="font-mono">{t.verify_code}</span></span>}</p>
        ))}
        {(issuedT || []).length === 0 && <p className="text-sm text-slate-400">No official transcripts issued yet.</p>}
      </Card>
    </div>
  );
};

export default function Gradebook() {
  const { t } = useI18n();
  const { data: sections } = useQuery({ queryKey: ['acad-sections-all'], queryFn: () => api.get('/acad/sections').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  return (
    <div className="space-y-5" data-testid="gradebook-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><Exam size={26} className="text-orange-600" />{t('Gradebook')}</h1>
        <p className="text-sm text-slate-500">{t('Enter marks, moderate, publish, and handle grade changes with a full paper trail.')}</p>
      </div>
      <Tabs defaultValue="grid">
        <TabsList className="h-auto flex-wrap">
          {[['grid', 'Grading grid'], ['queue', 'Moderation queue'], ['changes', 'Grade changes'], ['credentials', 'Credentials']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`gb-tab-${v}`} className="h-auto py-2">{t(l)}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="grid" className="mt-4"><GridTab sections={sections} /></TabsContent>
        <TabsContent value="queue" className="mt-4"><QueueTab /></TabsContent>
        <TabsContent value="changes" className="mt-4"><ChangesTab /></TabsContent>
        <TabsContent value="credentials" className="mt-4"><CredentialsTab students={students} /></TabsContent>
      </Tabs>
    </div>
  );
}
